import React, { useState, useEffect, useCallback } from 'react';

/* Shared full-screen lightbox used by every gallery demo.
   Use the hook in a page, spread its handlers, and render `lightbox`:

     const lb = useGalleryLightbox(PHOTOS);
     <Card onClick={() => lb.open(i)} />
     {lb.lightbox}
*/

export function GalleryLightbox({ photos, index, onClose, onPrev, onNext }) {
    // keyboard controls + lock body scroll while open
    useEffect(() => {
        if (index < 0) return undefined;
        const onKey = (e) => {
            if (e.key === 'Escape') onClose();
            else if (e.key === 'ArrowLeft') onPrev();
            else if (e.key === 'ArrowRight') onNext();
        };
        window.addEventListener('keydown', onKey);
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            window.removeEventListener('keydown', onKey);
            document.body.style.overflow = prevOverflow;
        };
    }, [index, onClose, onPrev, onNext]);

    if (index < 0) return null;
    const active = photos[index];

    return (
        <div className="jjfv2">
            <div className="glightbox" role="dialog" aria-modal="true" aria-label="Photo viewer" onClick={onClose}>
                <button className="glb__btn glb__close" onClick={onClose} aria-label="Close">
                    <svg viewBox="0 0 24 24" fill="none"><path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>
                </button>
                <button
                    className="glb__btn glb__nav glb__prev"
                    onClick={(e) => { e.stopPropagation(); onPrev(); }}
                    aria-label="Previous photo"
                >
                    <svg viewBox="0 0 24 24" fill="none"><path d="M15 6l-6 6 6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                </button>

                <figure className="glb__figure" onClick={(e) => e.stopPropagation()}>
                    <img src={active.src} alt={active.cap} />
                    <figcaption>
                        <span>{active.cap}</span>
                        <span className="n">{index + 1} / {photos.length}</span>
                    </figcaption>
                </figure>

                <button
                    className="glb__btn glb__nav glb__next"
                    onClick={(e) => { e.stopPropagation(); onNext(); }}
                    aria-label="Next photo"
                >
                    <svg viewBox="0 0 24 24" fill="none"><path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                </button>
            </div>
        </div>
    );
}

export function useGalleryLightbox(photos) {
    const [index, setIndex] = useState(-1);
    const open = useCallback((i) => setIndex(i), []);
    const close = useCallback(() => setIndex(-1), []);
    const prev = useCallback(() => setIndex((i) => (i <= 0 ? photos.length - 1 : i - 1)), [photos.length]);
    const next = useCallback(() => setIndex((i) => (i >= photos.length - 1 ? 0 : i + 1)), [photos.length]);

    const lightbox = (
        <GalleryLightbox photos={photos} index={index} onClose={close} onPrev={prev} onNext={next} />
    );

    return { open, close, prev, next, index, lightbox };
}

export default GalleryLightbox;
